/**
 * Conversores upcast/downcast para recorderBlock
 */
export function downcastRecorderAttribute(recorderUtils, recorderType, attributeKey) {
    return dispatcher => {
        dispatcher.on(`attribute:${attributeKey}:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;
            const writer = conversionApi.writer;
            const container = conversionApi.mapper.toViewElement(data.item);
            if (!container) return;

            const recorder = recorderUtils.findViewRecorderElement(container);
            if (!recorder) return;

            if (data.attributeNewValue) {
                writer.setAttribute(data.attributeKey, data.attributeNewValue, recorder);
            } else {
                writer.removeAttribute(data.attributeKey, recorder);
            }
        });
    };
}

/** width → style */
/** width → style */
export function downcastRecorderWidth(recorderType) {
    return dispatcher => {
        dispatcher.on(`attribute:width:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;

            const writer = conversionApi.writer;
            const figure = conversionApi.mapper.toViewElement(data.item);
            if (!figure) return;

            const newWidth = data.attributeNewValue;

            if (newWidth && newWidth !== 'Original') {
                writer.setStyle('width', newWidth, figure);
                writer.addClass('recorder_resized', figure);
            } else {
                writer.setStyle('width', '100%', figure);
                writer.removeClass('recorder_resized', figure);
            }

            // Asegurar que el <audio>/<video> no guarde width
            const media = Array.from(figure.getChildren()).find(
                child => child.is('element', 'audio') || child.is('element', 'video')
            );
            if (media) {
                writer.removeAttribute('width', media);
            }
        });
    };
}


/** resized → class */
export function downcastRecorderResized(recorderType) {
    return dispatcher => {
        dispatcher.on(`attribute:resized:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;
            const writer = conversionApi.writer;
            const container = conversionApi.mapper.toViewElement(data.item);
            if (!container) return;

            if (data.attributeNewValue) {
                writer.addClass('recorder_resized', container);
            } else {
                writer.removeClass('recorder_resized', container);
            }
        });
    };
}

/** alignment → clases */
export function downcastRecorderAlignment(recorderType) {
    const ALL_ALIGN_CLASSES = [
        'recorder-style-align-left',
        'recorder-style-align-right',
        'recorder-style-align-center'
    ];
    return dispatcher => {
        dispatcher.on(`attribute:alignment:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;
            const writer = conversionApi.writer;
            const container = conversionApi.mapper.toViewElement(data.item);
            if (!container) return;

            for (const cls of ALL_ALIGN_CLASSES) writer.removeClass(cls, container);

            if (data.attributeNewValue) {
                writer.addClass(`recorder-style-align-${data.attributeNewValue}`, container);
            }
        });
    };
}

/** flip → clases */
export function downcastRecorderFlip(recorderType) {
    const ALL_FLIP_CLASSES = [
        'recorder-flip-h',
        'recorder-flip-v',
        'recorder-rotate-90',
        'recorder-rotate-180'
    ];
    return dispatcher => {
        dispatcher.on(`attribute:flip:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;
            const writer = conversionApi.writer;
            const container = conversionApi.mapper.toViewElement(data.item);
            if (!container) return;

            for (const cls of ALL_FLIP_CLASSES) writer.removeClass(cls, container);

            if (data.attributeNewValue) {
                writer.addClass(`recorder-${data.attributeNewValue}`, container);
            }
        });
    };
}

/* Upcast alignment */
export function upcastRecorderAlignment(recorderType) {
    return dispatcher => {
        dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
            const viewElement = data.viewItem;
            const writer = conversionApi.writer;
            let alignment = null;
            if (viewElement.hasClass('recorder-style-align-left')) alignment = 'left';
            if (viewElement.hasClass('recorder-style-align-center')) alignment = 'center';
            if (viewElement.hasClass('recorder-style-align-right')) alignment = 'right';
            if (alignment && data.modelRange) {
                const modelElement = data.modelRange.getContainedElement?.() || data.modelRange.start.nodeAfter;
                if (modelElement) writer.setAttribute('alignment', alignment, modelElement);
            }
        });
    };
}

/* Upcast width */
export function upcastRecorderWidth(recorderType) {
    return dispatcher => {
        dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
            const viewElement = data.viewItem;
            const writer = conversionApi.writer;
            const width = viewElement.getStyle('width');

            if (!data.modelRange) return;

            const modelElement =
                data.modelRange.getContainedElement?.() || data.modelRange.start.nodeAfter;
            if (!modelElement) return;

            if (width) {
                if (width.trim() === '100%' || width.trim() === '100% ') {
                    writer.setAttribute('width', 'Original', modelElement);
                } else {
                    writer.setAttribute('width', width, modelElement);
                }
            } else {
                writer.setAttribute('width', 'Original', modelElement);
            }
        });
    };
}


/* Upcast resized */
export function upcastRecorderResized(recorderType) {
    return dispatcher => {
        dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
            const viewElement = data.viewItem;
            const writer = conversionApi.writer;
            if (viewElement.hasClass('recorder_resized') && data.modelRange) {
                const modelElement = data.modelRange.getContainedElement?.() || data.modelRange.start.nodeAfter;
                if (modelElement) writer.setAttribute('resized', true, modelElement);
            }
        });
    };
}

/* Upcast flip */
export function upcastRecorderFlip(recorderType) {
    return dispatcher => {
        dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
            const viewElement = data.viewItem;
            const writer = conversionApi.writer;
            let flip = null;
            if (viewElement.hasClass('recorder-flip-h')) flip = 'flip-h';
            if (viewElement.hasClass('recorder-flip-v')) flip = 'flip-v';
            if (viewElement.hasClass('recorder-rotate-90')) flip = 'rotate-90';
            if (viewElement.hasClass('recorder-rotate-180')) flip = 'rotate-180';
            if (flip && data.modelRange) {
                const modelElement = data.modelRange.getContainedElement?.() || data.modelRange.start.nodeAfter;
                if (modelElement) writer.setAttribute('flip', flip, modelElement);
            }
        });
    };
}


export function downcastRecorderOrder(recorderType) {
    return dispatcher => {
        dispatcher.on(`attribute:order:${recorderType}`, (evt, data, conversionApi) => {
            if (!conversionApi.consumable.consume(data.item, evt.name)) return;
            const writer = conversionApi.writer;
            const container = conversionApi.mapper.toViewElement(data.item);
            if (!container) return;

            if (data.attributeNewValue !== null) {
                writer.setStyle('order', data.attributeNewValue, container);
            } else {
                writer.removeStyle('order', container);
            }
        });
    };
}

export function upcastRecorderOrder(recorderType) {
    return dispatcher => {
        dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
            const viewElement = data.viewItem;
            const writer = conversionApi.writer;
            const order = viewElement.getStyle('order');
            if (order && data.modelRange) {
                const modelElement = data.modelRange.getContainedElement?.() || data.modelRange.start.nodeAfter;
                if (modelElement) writer.setAttribute('order', order, modelElement);
            }
        });
    };
}

export function upcastRecorderBlock() {
    return dispatcher => {
        dispatcher.on('element:figure', (evt, data, conversionApi) => {
            const viewElement = data.viewItem;

            // Solo procesa si tiene la clase recorder
            if (!viewElement.hasClass('recorder')) return;

            const writer = conversionApi.writer;
            const recorderEl = [...viewElement.getChildren()]
                .find(el => el.is('element', 'audio') || el.is('element', 'video'));

            if (!recorderEl) return;

            conversionApi.consumable.consume(viewElement, { name: true, classes: 'recorder' });

            // Atributos del <figure>
            const attrs = {};
            if (viewElement.getStyle('width')) {
                attrs.width = viewElement.getStyle('width');
            }
            if (viewElement.hasClass('recorder_resized')) {
                attrs.resized = true;
            }
            if (viewElement.hasClass('recorder-style-align-left')) attrs.alignment = 'left';
            if (viewElement.hasClass('recorder-style-align-center')) attrs.alignment = 'center';
            if (viewElement.hasClass('recorder-style-align-right')) attrs.alignment = 'right';

            // Atributos del <audio>/<video>
            attrs.src = recorderEl.getAttribute('src');
            attrs.type = recorderEl.name; // audio | video

            // Crea el nodo en el modelo
            const modelElement = writer.createElement('recorderBlock', attrs);

            // Inserta en el documento
            conversionApi.convertChildren(viewElement, modelElement);
            conversionApi.updateConversionResult(modelElement, data);
        });
    };
}

